'use client';

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProject } from '@/hooks/use-projects';
import { useUser } from '@/hooks/use-user';
import {
  MapPin,
  HandHeart,
  Loader2,
  Users,
  ArrowLeft,
  Send,
  Building2,
  Package,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HtmlRenderer } from '@/components/ui/html-renderer';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { formatNumber } from '@/lib/utils';
import type { ProjectItem } from '@/types';

function formatIndianRupee(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

export default function VendorProjectDetailPage() {
  const { communityId, projectId } = useParams<{ communityId: string; projectId: string }>();
  const navigate = useNavigate();
  const { project, isLoading, isError, mutate } = useProject(communityId || '', projectId || '');
  const { user } = useUser();
  const [isProposalOpen, setIsProposalOpen] = useState(false);
  const [proposalMessage, setProposalMessage] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [itemMessages, setItemMessages] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleProposalOpenChange = (open: boolean) => {
    if (open && selectedItemIds.length === 0) {
      toast.error('Select at least one item to propose for');
      return;
    }
    setIsProposalOpen(open);
  };

  const toggleItemSelection = (itemId: string, checked: boolean) => {
    setSelectedItemIds((prev) =>
      checked ? [...new Set([...prev, itemId])] : prev.filter((id) => id !== itemId)
    );
    if (!checked) {
      setItemMessages((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  const selectedItems = project?.items?.filter((item) => selectedItemIds.includes(item.id)) ?? [];

  const handleSubmitProposal = async () => {
    if (!project || !communityId) return;
    if (selectedItemIds.length === 0) {
      toast.error('Select at least one item to propose for');
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${project.id}/proposal`, {
        message: proposalMessage.trim() || undefined,
        items: selectedItems.map((item) => ({
          itemId: item.id,
          message: (itemMessages[item.id] ?? '').trim(),
        })),
      });
      toast.success('Interest submitted successfully');
      setIsProposalOpen(false);
      setProposalMessage('');
      setSelectedItemIds([]);
      setItemMessages({});
      mutate();
      navigate(`/${communityId}/dashboard?view=projects`);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to submit proposal');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-muted-foreground font-medium animate-pulse">Loading project...</p>
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-destructive font-medium">Failed to load project</p>
        <Button asChild variant="outline">
          <Link to={`/${communityId}/dashboard?view=projects`}>Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  const hasProjectDetails =
    Boolean(project.description) ||
    Boolean(project.needAssessment) ||
    Boolean(project.location) ||
    Boolean(project.happeningFor) ||
    Boolean(project.hostClub) ||
    Boolean(project.expectedCost) ||
    Boolean(project.estimatedBeneficiaries);

  const items = (project.items ?? []) as ProjectItem[];

  return (
    <div className="space-y-4 pb-12">
      <Button variant="ghost" asChild className="-ml-2">
        <Link to={`/${communityId}/dashboard?view=projects`}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Link>
      </Button>

      <Card className="overflow-hidden border-primary/15">
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <CardTitle className="text-2xl">{project.name}</CardTitle>
              {project.community && (
                <p className="text-muted-foreground">By {project.community.name}</p>
              )}
            </div>
            {project.isOpenForVendors && <Badge className="bg-green-500">Open for Vendors</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-4 sm:p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Items required</h3>
              </div>
              <div className="flex items-center gap-2">
                {project.isOpenForVendors ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs"
                      onClick={() => setSelectedItemIds(items.map((item) => item.id))}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs"
                      onClick={() => {
                        setSelectedItemIds([]);
                        setItemMessages({});
                      }}
                    >
                      Clear
                    </Button>
                  </>
                ) : null}
                <Badge variant="secondary">
                  {selectedItemIds.length > 0
                    ? `${selectedItemIds.length} selected`
                    : `${items.length} item${items.length === 1 ? '' : 's'}`}
                </Badge>
              </div>
            </div>
            {project.isOpenForVendors ? (
              <p className="text-sm text-foreground">
                Select the items you want to propose for, then continue below. Notes are optional —
                you can just show interest.
              </p>
            ) : null}
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items listed yet.</p>
            ) : (
              <ul className="divide-y divide-foreground/20">
                {items.map((item) => {
                  const checked = selectedItemIds.includes(item.id);
                  return (
                    <li key={item.id}>
                      <label className="flex cursor-pointer items-start gap-3 py-4">
                        {project.isOpenForVendors ? (
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) =>
                              toggleItemSelection(item.id, value === true)
                            }
                            className="mt-1"
                          />
                        ) : null}
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-semibold text-foreground">{item.name}</p>
                          {item.description ? (
                            <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
                              {item.description}
                            </p>
                          ) : null}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-base font-semibold text-primary">
                            {formatNumber(item.quantity)}
                            {item.unit ? (
                              <span className="ml-1 text-sm font-normal text-muted-foreground">
                                {item.unit}
                              </span>
                            ) : null}
                          </p>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {project.isOpenForVendors && (
            <Dialog open={isProposalOpen} onOpenChange={handleProposalOpenChange}>
              <Button
                className="w-full"
                size="lg"
                disabled={selectedItemIds.length === 0}
                onClick={() => handleProposalOpenChange(true)}
              >
                <Send className="mr-2 h-4 w-4" />
                Submit Proposal
                {selectedItemIds.length > 0 ? ` (${selectedItemIds.length})` : ''}
              </Button>
              <DialogContent
                className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <DialogHeader>
                  <DialogTitle>Submit Proposal</DialogTitle>
                  <DialogDescription>
                    Optionally add a note for each item, or submit to show interest.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-3">
                    {selectedItems.map((item, index) => (
                      <div
                        key={item.id}
                        className="space-y-2 rounded-xl border border-foreground/20 bg-muted/30 p-3"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                            {index + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <Label
                              htmlFor={`item-note-${item.id}`}
                              className="text-sm font-semibold text-foreground"
                            >
                              {item.name}
                            </Label>
                            <p className="text-xs text-foreground">
                              Qty: {formatNumber(item.quantity)}
                              {item.unit ? ` ${item.unit}` : ''}
                            </p>
                          </div>
                        </div>
                        <Input
                          id={`item-note-${item.id}`}
                          value={itemMessages[item.id] ?? ''}
                          onChange={(e) =>
                            setItemMessages((prev) => ({
                              ...prev,
                              [item.id]: e.target.value,
                            }))
                          }
                          placeholder="Optional note for this item"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label htmlFor="proposal-message">Additional notes (optional)</Label>
                    <Textarea
                      id="proposal-message"
                      value={proposalMessage}
                      onChange={(e) => setProposalMessage(e.target.value)}
                      placeholder="Anything else the project team should know"
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                  <Button
                    onClick={handleSubmitProposal}
                    disabled={isSubmitting || selectedItemIds.length === 0}
                    className="w-full"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Proposal'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {project.isOpenForVendors && (!user?.businesses || user.businesses.length === 0) && (
            <div className="text-center text-muted-foreground text-sm">
              Add your business to submit proposals for this project.
            </div>
          )}

          {hasProjectDetails ? (
            <Accordion type="single" collapsible className="rounded-xl border px-4">
              <AccordionItem value="details" className="border-0">
                <AccordionTrigger className="hover:no-underline">Project details</AccordionTrigger>
                <AccordionContent className="space-y-5">
                  {project.description && (
                    <div>
                      <h4 className="font-medium mb-2">Description</h4>
                      <HtmlRenderer html={project.description} />
                    </div>
                  )}

                  {project.needAssessment && (
                    <div>
                      <h4 className="font-medium mb-2">Need Assessment</h4>
                      <HtmlRenderer html={project.needAssessment} />
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    {project.location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>{project.location}</span>
                      </div>
                    )}
                    {project.happeningFor && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>For: {project.happeningFor}</span>
                      </div>
                    )}
                    {project.hostClub && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>Host club: {project.hostClub}</span>
                      </div>
                    )}
                    {project.expectedCost != null && (
                      <div className="flex items-center gap-2 text-sm">
                        <HandHeart className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>{formatIndianRupee(project.expectedCost)}</span>
                      </div>
                    )}
                    {project.estimatedBeneficiaries != null && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>{formatNumber(project.estimatedBeneficiaries)} beneficiaries</span>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
